import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BlackTriangleIcon } from "../../asset/image/icon";
import { useSelectTheme } from "../../store/state";

export interface CursorTooltipProps {
  open: boolean;
  x: number;
  y: number;
  text: string;
}

export const CursorTooltip = ({ open, x, y, text }: CursorTooltipProps) => {
  const { color, fontSize } = useSelectTheme();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (!open || !ref.current) return;

    const offset = 18;
    const margin = 8;
    const el = ref.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Position above cursor, horizontally centered (tooltip top-left)
    let left = x - rect.width / 2;
    let top = y - offset - rect.height;

    // --- Clamp within viewport: left/right ---
    if (left < margin) left = margin;
    if (left + rect.width > vw - margin) left = vw - margin - rect.width;

    // --- Clamp within viewport: top/bottom ---
    if (top < margin) {
      top = y + offset;
    }

    // --- Clamp within viewport: bottom ---
    if (top + rect.height > vh - margin) {
      top = vh - margin - rect.height;
    }

    setPos({ left, top });
  }, [open, x, y, text]);

  if (!open) return null;

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        zIndex: "9999",
        padding: "8px 12px",
        borderRadius: 4,
        bottom: 50,
        backgroundColor: color.black,
        boxShadow: "0 4px 10px 1px rgba(104, 104, 104, 0.25)",
        color: "#FFFFFF",
        fontSize: fontSize.h7,
        pointerEvents: "none",
        maxWidth: 244,
        maxHeight: 88,
      }}
    >
      <div
        css={{
          textAlign: "center",
          width: "200px",
          margin: "0 auto",
          color: "#FFF",
          whiteSpace: "pre-line",
        }}
      >
        {text}
      </div>
      <img
        height={13}
        css={{
          position: "fixed",
          left: x - 5,
          top: y - 20,
        }}
        src={BlackTriangleIcon}
        width="16"
        alt=""
        aria-hidden="true"
      />
    </div>,
    document.body,
  );
};
